import React, { Fragment, useMemo } from 'react';
import { Box, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';
import LinearProgress from '@material-ui/core/LinearProgress';
import { List, Pagination, useListContext, useTranslate } from 'react-admin';
import { AVATAR_PROPERTY } from '../../../providers/resources';
import { extractIdFromURI } from '../../../utils/resources/ids';
import { AvatarProperty } from '../../../utils/types';
import { CRM_COLORS } from '../../../theme/crmColors';
import CollapsePanel from '../components/CollapsePanel';
import InfoItem from './components/InfoItem';

const useStyles = makeStyles({
    defHeader: {
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.3px',
        color: CRM_COLORS.text.secondary,
    },
    name: {
        fontWeight: 500,
    },
    value: {
        color: 'rgba(0,0,0,0.6)',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
});

interface AdditionalInfoPanelProps {
    selected: any;
}

const groupByDefinition = (items: AvatarProperty[]): { definition: string; data: AvatarProperty[] }[] => {
    const mapDef: Record<string, AvatarProperty[]> = {};
    items.forEach((item) => {
        const def = item.definition || '';
        if (!mapDef[def]) mapDef[def] = [];
        mapDef[def].push(item);
    });
    return Object.keys(mapDef).map((def) => ({ definition: def, data: mapDef[def] }));
};

const PropertiesListBody: React.FC = () => {
    const classes = useStyles();
    const t = useTranslate();
    const { ids, data, loading } = useListContext<AvatarProperty>();

    if (loading) return <LinearProgress />;

    const records: AvatarProperty[] = (ids || []).map((id: any) => data[id]).filter(Boolean);

    if (!records || records.length === 0)
        return (
            <Box width='100%' textAlign='center'>
                <Typography variant='body2' color='textSecondary'>
                    ---
                </Typography>
            </Box>
        );

    const groups = groupByDefinition(records);

    const formatValue = (v: any): string => {
        if (v === true) return t('app.yes') || 'Yes';
        if (v === false) return t('app.no') || 'No';
        if (v === null || v === undefined) return '';
        return String(v);
    };

    return (
        <>
            {groups.map(({ definition, data }) => (
                <Box
                    key={definition}
                    style={{
                        display: 'grid',
                        gap: 8,
                    }}
                >
                    <Typography variant='subtitle2' className={classes.defHeader}>
                        {definition}
                    </Typography>

                    {data.map((prop, idx) => {
                        const valueStr = `${formatValue(prop.value) ?? ''} ${prop.unit ?? ''}`.trim();

                        return (
                            <Fragment key={prop.id as any}>
                                <InfoItem label={prop.name} value={valueStr || t('app.no') || '-'} />

                                {idx < data.length - 1 && <Divider />}
                            </Fragment>
                        );
                    })}
                </Box>
            ))}

            <Pagination />
        </>
    );
};

const AdditionalInfoPanel: React.FC<AdditionalInfoPanelProps> = ({ selected }) => {
    const t = useTranslate();

    const selectedId: string | undefined = useMemo(
        () => (selected ? ((selected.avatarId || selected.id) as string) : undefined),
        [selected],
    );
    const avatarId = selectedId ? extractIdFromURI(selectedId) : undefined;

    return (
        <CollapsePanel
            icon={<InfoIcon fontSize='small' />}
            title={t('app.objects.filters.avatarProperties.additionalInformation') || 'Propiedades'}
        >
            <Box
                style={{
                    width: '100%',
                    overflowX: 'auto',
                }}
            >
                <List
                    component='div'
                    // Using List instead of ListBase to align with project usage
                    resource={AVATAR_PROPERTY}
                    basePath={`/${AVATAR_PROPERTY}`}
                    filter={avatarId ? { 'avatar.id': avatarId } : {}}
                    sort={{ field: 'creationDate', order: 'DESC' }}
                    pagination={<></>}
                    actions={false}
                    bulkActionButtons={false}
                    empty={false}
                >
                    <PropertiesListBody />
                </List>
            </Box>
        </CollapsePanel>
    );
};

export default AdditionalInfoPanel;
